import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion } from 'motion/react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  ChartBarIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  Search,
  Users,
  ClipboardList,
  BarChart3,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Library as LibraryIcon,
  Plus,
  Eye,
  MoreVertical,
} from 'lucide-react';
import { libraryApi } from '../../lib/api/libraryApi';
import { useAuth } from '../../contexts/AuthContext';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import AssessmentCreateFlowModal from '../assessments/AssessmentCreateFlowModal';

const DIFFICULTY_COLORS = {
  Easy: 'bg-emerald-100 text-emerald-800',
  Medium: 'bg-amber-100 text-amber-800',
  Hard: 'bg-rose-100 text-rose-800',
};

const TestLibrary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const isLibraryAssessments = location.pathname.startsWith('/dashboard/library/assessments');

  const canDeleteLibraryAssessment = (row) => {
    const uid = authUser?._id || authUser?.id;
    if (!uid || !row?.createdBy) return false;
    if (String(row.createdBy) === String(uid)) return true;
    return (
      authUser?.accountType === 'org' &&
      (authUser?.role === 'admin' || authUser?.role === 'org_admin')
    );
  };
  const detailPath = (id) => `/dashboard/library/assessments/${id}`;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewTestId, setViewTestId] = useState(null); // For opening view-only modal
  const [editTestId, setEditTestId] = useState(null); // For opening edit modal
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null); // { id, top, left } | null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, title } | null
  // Personality tests can only be renamed — their 50 statements are fixed.
  const [renameTarget, setRenameTarget] = useState(null); // { id, title } | null
  const [renameValue, setRenameValue] = useState('');
  useBodyScrollLock(
    createModalOpen || Boolean(viewTestId) || Boolean(editTestId) || Boolean(deleteConfirm) || Boolean(renameTarget)
  );
  const queryClient = useQueryClient();

  const closeActionsMenu = useCallback(() => setActionsMenuAnchor(null), []);

  const deleteLibraryMutation = useMutation({
    mutationFn: (id) => libraryApi.deleteLibraryAssessment(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['library-assessments-stats'] });
      queryClient.invalidateQueries({ queryKey: ['library-assessments-filters'] });
      toast.success('Assessment removed from your library');
      setDeleteConfirm(null);
      if (viewTestId && String(viewTestId) === String(deletedId)) {
        setViewTestId(null);
      }
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || 'Could not delete this assessment';
      toast.error(msg);
    },
  });

  const renameLibraryMutation = useMutation({
    mutationFn: ({ id, title }) => libraryApi.updateLibraryAssessment(id, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library-assessments-stats'] });
      toast.success('Assessment renamed');
      setRenameTarget(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not rename this assessment');
    },
  });

  useEffect(() => {
    if (!actionsMenuAnchor) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeActionsMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actionsMenuAnchor, closeActionsMenu]);

  const limit = isLibraryAssessments ? 20 : 12;

  // Debounce search for assessments library
  useEffect(() => {
    if (!isLibraryAssessments) return;
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm, isLibraryAssessments]);

  // Open create modal from external creation link
  useEffect(() => {
    if (!isLibraryAssessments) return;
    const params = new URLSearchParams(location.search);
    if (params.get('externalCreate') !== '1') return;

    setCreateModalOpen(true);
    params.delete('externalCreate');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }
    );
  }, [isLibraryAssessments, location.pathname, location.search, navigate]);

  // Assessments Library: use stats API (team/org-scoped on server)
  const { data: assessmentsData, isLoading: loadingAssessments } = useQuery({
    queryKey: ['library-assessments-stats', currentPage, debouncedSearch, categoryFilter, difficultyFilter],
    queryFn: () =>
      libraryApi.getAssessmentsStats({
        page: currentPage,
        limit,
        search: debouncedSearch || undefined,
        category: categoryFilter || undefined,
        difficulty: difficultyFilter || undefined,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: isLibraryAssessments,
  });

  const { data: assessmentsFiltersData } = useQuery({
    queryKey: ['library-assessments-filters'],
    queryFn: () => libraryApi.getAssessmentsFilters(),
    staleTime: 10 * 60 * 1000,
    enabled: isLibraryAssessments,
  });

  // Legacy Test Library: filters and list
  const { data: availableFilters = {} } = useQuery({
    queryKey: ['test-filters'],
    queryFn: async () => {
      const response = await axios.get('/api/tests/library-filters');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: !isLibraryAssessments,
  });

  const { data: testsData, isLoading: loadingTests } = useQuery({
    queryKey: ['tests-library', currentPage, filters, searchTerm],
    queryFn: async () => {
      const params = { page: currentPage, limit, ...filters };
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get('/api/tests/library', { params });
      return { tests: response.data.tests || [], pagination: response.data.pagination || {} };
    },
    staleTime: 2 * 60 * 1000,
    enabled: !isLibraryAssessments,
  });

  const assessments = assessmentsData?.assessments || [];
  const assessmentsPagination = assessmentsData?.pagination || { current: 1, total: 1, totalCount: 0 };
  const tests = testsData?.tests || [];
  const pagination = testsData?.pagination || {};

  const assessmentCategoryOptions =
    (assessmentsFiltersData?.categories && assessmentsFiltersData.categories.length > 0
      ? assessmentsFiltersData.categories
      : ['Sales', 'Software Development', 'Marketing', 'General', 'IT', 'Engineering']);

  const assessmentDifficultyOptions =
    (assessmentsFiltersData?.difficulties && assessmentsFiltersData.difficulties.length > 0
      ? assessmentsFiltersData.difficulties
      : ['Easy', 'Medium', 'Hard']);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Hard':
        return 'bg-red-100 text-red-800';
      default:
        return DIFFICULTY_COLORS[difficulty] || 'bg-gray-100 text-gray-800';
    }
  };

  const formatCategoryLabel = (category) => {
    if (category == null) return '—';
    const s = String(category).trim();
    if (!s) return '—';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getRoleIcon = (role) => {
    const roleIcons = {
      Accountant: '💰',
      HR: '👥',
      Taxation: '📊',
      Legal: '⚖️',
      Marketing: '📢',
      Sales: '💼',
      Administration: '🏢',
      Finance: '💳',
      MR: '💊',
      IT: '💻',
      Engineering: '🔧',
      Healthcare: '🏥',
      Education: '📚',
      Retail: '🛍️',
      Manufacturing: '🏭',
    };
    return roleIcons[role] || '📋';
  };

  const loading = isLibraryAssessments ? loadingAssessments : loadingTests;

  // ----- Assessments Library (table UI) -----
  if (isLibraryAssessments) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/50"
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
          {/* Header + Create button */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl bg-[#2563eb]/10">
                <LibraryIcon className="h-8 w-8 text-[#2563eb]" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  Assessments Library
                </h1>
                <p className="text-gray-600 text-sm sm:text-base mt-0.5">
                  Browse your assessments with usage stats across screenings and candidates.
                </p>
              </div>
            </div>
            <button
              type="button"
              data-testid="create-assessment-btn"
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-[#1d4ed8] transition-colors shadow-sm"
            >
              <Plus className="h-5 w-5" />
              Create Assessments
            </button>
          </div>

          {/* Search & Filters */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by title or content..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 py-3 bg-white border border-gray-200 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] hover:border-gray-300 transition-colors duration-200"
              />
              {searchTerm && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                  {assessmentsPagination.totalCount} result{assessmentsPagination.totalCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] text-gray-700"
              >
                <option value="">All categories</option>
                {assessmentCategoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {formatCategoryLabel(c)}
                  </option>
                ))}
              </select>

            </div>
          </div>

          {loadingAssessments ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-24"
            >
              <div className="h-10 w-10 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm font-medium text-gray-600">Loading your assessments...</p>
            </motion.div>
          ) : assessments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16 px-6"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2563eb]/10 mb-4">
                <BookOpen className="h-8 w-8 text-[#2563eb]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No assessments yet</h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                Create an assessment with the button above or save one from a screening to reuse in future screenings.
              </p>
              <button
                type="button"
                data-testid="create-assessment-empty-btn"
                onClick={() => setCreateModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-[#1d4ed8] transition-colors"
              >
                <Plus className="h-5 w-5" />
                Create Assessments
              </button>
            </motion.div>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="assessment-library-table">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/80">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Assessment
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Screenings
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Answered
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Avg
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Median
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessments.map((row, idx) => (
                        <motion.tr
                          key={row._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2, delay: idx * 0.02 }}
                          className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="py-4 px-4">
                            <button
                              type="button"
                              // onClick={() => navigate(detailPath(row._id))}
                              className="text-left block group"
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-medium text-gray-900 group-hover:text-[#2563eb]">
                                  {row.title}
                                </h3>
                                {row.isRubricBased && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 whitespace-nowrap">
                                    Rubric Assessment
                                  </span>
                                )}
                                {row.isSystem && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">
                                    Built-in
                                  </span>
                                )}
                                {row.assessmentKind === 'personality-ipip50' && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 whitespace-nowrap">
                                    Personality
                                  </span>
                                )}
                                {row.isMultiModule && !row.isRubricBased && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 whitespace-nowrap">
                                    Multi Module
                                  </span>
                                )}
                              </div>
                              {row.description && (
                                <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{row.description}</p>
                              )}
                            </button>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="inline-flex items-center gap-1 text-sm text-gray-700">
                              {/* <ClipboardList className="h-4 w-4 text-[#2563eb] shrink-0" /> */}
                              <span>{row.stats?.screeningsCount ?? 0}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                              {/* <Users className="h-4 w-4 text-gray-400" /> */}
                              {row.stats?.totalAnswered ?? 0}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-[#2563eb]">
                              {/* <BarChart3 className="h-4 w-4" /> */}
                              {row.stats?.avgScorePercent != null ? `${row.stats.avgScorePercent}%` : '—'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                              {/* <BarChart3 className="h-4 w-4 text-gray-400" /> */}
                              {row.stats?.medianScorePercent != null ? `${row.stats.medianScorePercent}%` : '—'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-600">{formatCategoryLabel(row.category)}</td>
                          <td className="py-4 px-4 text-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const menuWidth = 160;
                                const left = Math.min(
                                  Math.max(8, rect.right - menuWidth),
                                  window.innerWidth - menuWidth - 8
                                );
                                const idStr = String(row._id);
                                setActionsMenuAnchor((cur) =>
                                  cur?.id === idStr ? null : { id: idStr, top: rect.bottom + 4, left }
                                );
                              }}
                              className="p-2 rounded-lg text-gray-500 hover:text-[#2563eb] hover:bg-[#2563eb]/10 transition-colors"
                              title="Actions"
                              aria-label="Open actions menu"
                              aria-expanded={actionsMenuAnchor?.id === String(row._id)}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {assessmentsPagination.total > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Showing page {assessmentsPagination.current} of {assessmentsPagination.total} (
                    {assessmentsPagination.totalCount} assessments)
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={assessmentsPagination.current <= 1}
                      className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <span className="text-sm font-medium text-gray-700">
                      {assessmentsPagination.current} / {assessmentsPagination.total}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(assessmentsPagination.total, p + 1))
                      }
                      disabled={assessmentsPagination.current >= assessmentsPagination.total}
                      className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}

              {actionsMenuAnchor &&
                createPortal(
                  (() => {
                    const rowForMenu = assessments.find((a) => String(a._id) === actionsMenuAnchor.id);
                    return (
                      <>
                        <div className="fixed inset-0 z-[199]" aria-hidden onClick={closeActionsMenu} />
                        <div
                          className="fixed z-[200] w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-xl text-left"
                          style={{ top: actionsMenuAnchor.top, left: actionsMenuAnchor.left }}
                          role="menu"
                        >
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => {
                              setViewTestId(actionsMenuAnchor.id);
                              closeActionsMenu();
                            }}
                          >
                            <Eye className="h-4 w-4 shrink-0" />
                            View
                          </button>
                          {rowForMenu?.assessmentKind === 'personality-ipip50' && !rowForMenu?.isSystem && (
                            <button
                              type="button"
                              data-testid="library-rename-btn"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => {
                                setRenameTarget({ id: actionsMenuAnchor.id, title: rowForMenu?.title || '' });
                                setRenameValue(rowForMenu?.title || '');
                                closeActionsMenu();
                              }}
                            >
                              <PencilIcon className="h-4 w-4 shrink-0" />
                              Rename
                            </button>
                          )}
                          {(!rowForMenu?.isMultiModule || rowForMenu?.isRubricBased) &&
                            !rowForMenu?.isSystem &&
                            rowForMenu?.assessmentKind !== 'personality-ipip50' && (
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => {
                                setEditTestId(actionsMenuAnchor.id);
                                closeActionsMenu();
                              }}
                            >
                              <PencilIcon className="h-4 w-4 shrink-0" />
                              Edit
                            </button>
                          )}
                          {canDeleteLibraryAssessment(rowForMenu) && !rowForMenu?.isSystem && (
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
                              onClick={() => {
                                setDeleteConfirm({
                                  id: actionsMenuAnchor.id,
                                  title: rowForMenu?.title || 'this assessment',
                                });
                                closeActionsMenu();
                              }}
                            >
                              <TrashIcon className="h-4 w-4" />
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </>
                    );
                  })(),
                  document.body
                )}

              {deleteConfirm &&
                createPortal(
                  <div className="fixed inset-0 z-[210] flex items-center justify-center px-4">
                    <div
                      className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
                      onClick={() => !deleteLibraryMutation.isPending && setDeleteConfirm(null)}
                      aria-hidden
                    />
                    <div
                      className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="delete-library-title"
                    >
                      <h2 id="delete-library-title" className="text-lg font-semibold text-gray-900">
                        Delete from library?
                      </h2>
                      <p className="mt-1 text-sm font-medium text-gray-800 line-clamp-2">
                        “{deleteConfirm.title}”
                      </p>
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                        This cannot be undone. You won’t see this template again or its usage stats here.{' '}
                        <span className="font-medium text-gray-800">Active screenings already using it</span> are not
                        affected.
                      </p>
                      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                        <button
                          type="button"
                          className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          disabled={deleteLibraryMutation.isPending}
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-4 py-2.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                          disabled={deleteLibraryMutation.isPending}
                          onClick={() => deleteLibraryMutation.mutate(deleteConfirm.id)}
                        >
                          {deleteLibraryMutation.isPending ? 'Deleting…' : 'Yes, delete'}
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}

              {renameTarget &&
                createPortal(
                  <div className="fixed inset-0 z-[210] flex items-center justify-center px-4">
                    <div
                      className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
                      aria-hidden
                      onClick={() => !renameLibraryMutation.isPending && setRenameTarget(null)}
                    />
                    <form
                      className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="rename-library-title"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const title = renameValue.trim();
                        if (title) renameLibraryMutation.mutate({ id: renameTarget.id, title });
                      }}
                    >
                      <h2 id="rename-library-title" className="text-lg font-semibold text-gray-900">
                        Rename assessment
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        Only the name changes — the 50 statements are fixed. Screenings that already use this test keep
                        their current name.
                      </p>
                      <input
                        data-testid="library-rename-input"
                        autoFocus
                        maxLength={100}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className="mt-4 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                        <button
                          type="button"
                          className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          disabled={renameLibraryMutation.isPending}
                          onClick={() => setRenameTarget(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          data-testid="library-rename-save"
                          className="px-4 py-2.5 rounded-lg bg-[#007BFF] text-white text-sm font-medium hover:bg-[#0062CC] disabled:opacity-50"
                          disabled={renameLibraryMutation.isPending || !renameValue.trim()}
                        >
                          {renameLibraryMutation.isPending ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </form>
                  </div>,
                  document.body
                )}
            </>
          )}

          {createModalOpen && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 transition-opacity duration-200 bg-black/40 backdrop-blur-[2px]">
              <div className="fixed inset-0 transition-opacity" aria-hidden />
              <div
                className="relative flex flex-col w-full max-w-4xl h-[70vh] max-h-[70vh] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out"
                style={{ minHeight: '580px' }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <AssessmentCreateFlowModal
                  onClose={() => {
                    setCreateModalOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['library-assessments-stats'] });
                  }}
                  skipInviteStep
                  hasNextStep={false}
                  enableCreatorSelection
                />
              </div>
            </div>
          )}

          {/* View-only modal for viewing assessment questions */}
          {viewTestId && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 transition-opacity duration-200 bg-black/40 backdrop-blur-[2px]">
              <div
                className="fixed inset-0 transition-opacity"
                onClick={() => setViewTestId(null)}
                aria-hidden
              />
              <div
                className="relative flex flex-col w-full max-w-4xl h-[90vh] max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out"
                style={{ minHeight: '580px' }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <AssessmentCreateFlowModal
                  onClose={() => setViewTestId(null)}
                  skipInviteStep
                  hasNextStep={false}
                  viewOnly
                  viewTestId={viewTestId}
                />
              </div>
            </div>
          )}

          {/* Edit modal — reuses the shared AssessmentCreateFlowModal in edit-library mode.
              Only updates the library Test (template). Existing screenings keep their original questions. */}
          {editTestId && (
            <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40">
              <div className="relative w-full max-w-6xl h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl">
                <AssessmentCreateFlowModal
                  onClose={(result) => {
                    setEditTestId(null);
                    if (result?.didEdit) {
                      queryClient.invalidateQueries({ queryKey: ['library-assessments-stats'] });
                      queryClient.invalidateQueries({ queryKey: ['library-assessments-filters'] });
                    }
                  }}
                  skipInviteStep
                  hasNextStep={false}
                  editLibraryTestId={editTestId}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // ----- Legacy Test Library (grid) -----
  if (loading && tests.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Test Library</h1>
          <p className="text-gray-600">
            Browse our collection of pre-built assessments across various industries and roles.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tests by title, description, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 transition-colors"
              >
                Search
              </button>
            </div>
          </form>

          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filters</span>
            </button>
            {(Object.keys(filters).length > 0 || searchTerm) && (
              <button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-800 transition-colors">
                Clear all filters
              </button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={filters.role || ''}
                  onChange={(e) => handleFilterChange('role', e.target.value || undefined)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Roles</option>
                  {availableFilters.roles?.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time (minutes)</label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minTime || ''}
                    onChange={(e) => handleFilterChange('minTime', e.target.value || undefined)}
                    className="w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxTime || ''}
                    onChange={(e) => handleFilterChange('maxTime', e.target.value || undefined)}
                    className="w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Questions</label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minQuestions || ''}
                    onChange={(e) => handleFilterChange('minQuestions', e.target.value || undefined)}
                    className="w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxQuestions || ''}
                    onChange={(e) => handleFilterChange('maxQuestions', e.target.value || undefined)}
                    className="w-1/2 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            {pagination.totalTests
              ? `${pagination.totalTests} test${pagination.totalTests !== 1 ? 's' : ''} found`
              : 'No tests found'}
          </p>
        </div>

        {tests.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tests.map((test) => (
              <div
                key={test._id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getRoleIcon(test.role)}</span>

                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{test.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {test.description || 'No description available'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <UserIcon className="h-4 w-4 mr-2" />
                      <span>{test.role}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      <span>{test.estimatedTime} minutes</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <QuestionMarkCircleIcon className="h-4 w-4 mr-2" />
                      <span>{test.numberOfQuestions} questions</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <ChartBarIcon className="h-4 w-4 mr-2" />
                      <span>{test.totalPoints} points</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={() => navigate(detailPath(test._id))}
                      className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <EyeIcon className="h-4 w-4 mr-2" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <QuestionMarkCircleIcon className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tests found</h3>
            <p className="text-gray-600">Try adjusting your search criteria or filters to find more tests.</p>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2 mt-8">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={!pagination.hasPrev}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.totalPages))}
              disabled={!pagination.hasNext}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestLibrary;
